import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, User, ChevronUp } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface ChatMessage {
  role: "user" | "bot";
  text: string;
  action?: string;
  endpoint?: string;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "bot", text: "Hi! I'm AlphaBot. Say 'help' to see what I can do, or just tell me to 'start bot'." },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const executeAction = async (msg: any) => {
    if (!msg.action || msg.action === "NONE" || !msg.endpoint) return;
    try {
      const url = `${API_BASE}${msg.endpoint}`;
      let r;
      if (msg.action === "POST") {
        r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...msg.params }) });
      } else if (msg.action === "GET") {
        const params = new URLSearchParams(msg.params || {}).toString();
        r = await fetch(`${url}${params ? "?" + params : ""}`);
      } else {
        return;
      }
      const data = await r.json();
      setMessages((prev) => [...prev, { role: "bot", text: `Done. Result: ${JSON.stringify(data).slice(0, 200)}` }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "bot", text: "Action failed. API might be offline." }]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userText = input.trim();
    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setInput("");
    setThinking(true);

    try {
      const r = await fetch(`${API_BASE}/api/chat/command`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userText }),
      });
      const data = await r.json();
      setMessages((prev) => [...prev, { role: "bot", text: data.speak || data.response, action: data.action, endpoint: data.endpoint }]);
      executeAction(data);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "bot", text: "I'm having trouble connecting. Is the backend running?" }]);
    }
    setThinking(false);
  };

  const quickActions = [
    "start bot",
    "stop bot",
    "show arbitrage",
    "show portfolio",
    "run cycle",
    "emergency stop",
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-terminal-accent text-black rounded-full shadow-lg flex items-center justify-center hover:bg-green-300 transition-all z-50"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-[#0c0c0c] border border-terminal-border rounded-lg shadow-2xl flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-terminal-border">
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-terminal-accent" />
              <span className="text-sm font-bold text-white">AlphaBot Agent</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white">
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "bot" && (
                  <div className="w-6 h-6 rounded bg-terminal-accent/20 flex items-center justify-center flex-shrink-0">
                    <Bot size={12} className="text-terminal-accent" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded text-xs ${
                    m.role === "user"
                      ? "bg-terminal-accent/20 text-terminal-accent border border-terminal-accent/30"
                      : "bg-terminal-dim text-gray-300 border border-terminal-border"
                  }`}
                >
                  {m.text}
                </div>
                {m.role === "user" && (
                  <div className="w-6 h-6 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <User size={12} className="text-gray-300" />
                  </div>
                )}
              </div>
            ))}
            {thinking && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded bg-terminal-accent/20 flex items-center justify-center">
                  <Bot size={12} className="text-terminal-accent animate-pulse" />
                </div>
                <div className="bg-terminal-dim border border-terminal-border rounded px-3 py-2 text-xs text-gray-400">Thinking...</div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick actions */}
          <div className="px-3 py-2 border-t border-terminal-border flex gap-2 overflow-auto">
            {quickActions.map((qa) => (
              <button
                key={qa}
                onClick={() => {
                  setInput(qa);
                }}
                className="text-[10px] px-2 py-1 border border-terminal-border rounded whitespace-nowrap text-gray-400 hover:text-terminal-accent hover:border-terminal-accent transition-all"
              >
                {qa}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-terminal-border flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Say 'start bot' or 'show arbitrage'..."
              className="terminal-input flex-1 text-xs"
            />
            <button onClick={sendMessage} className="terminal-btn-primary px-3">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
